import type { SiteConfig } from "./types";
import { defaultAnimation, defaultStyle } from "./types";

export const BASELINE: SiteConfig = {
  version: 2,
  theme: {
    background: "#f3f0e8",
    surface: "#fffdf8",
    text: "#171714",
    muted: "#6f6c63",
    primary: "#ff5c35",
    secondary: "#163f3a",
    accent: "#d7ff5f",
    buttonText: "#ffffff",
    border: "#cfcbc0",
    radius: 22,
  },
  brand: {
    name: "Sandbox",
    icon: "sandbox",
    ariaLabel: "Sandbox home",
  },
  globalContent: {
    footerDescription: "A premium website with a beautifully simple visual editor.",
    contactLabel: "Start something",
    email: "hello@sandbox.studio",
    location: "Zürich, Switzerland",
    socialLabel: "Elsewhere",
    instagramLabel: "Instagram ↗",
    instagramHref: "#top",
    linkedinLabel: "LinkedIn ↗",
    linkedinHref: "#top",
    copyright: "© 2026 Sandbox",
    deliveryNote: "Made with care. Delivered by Cloudflare.",
    backToTop: "Back to top ↑",
  },
  globalTextStyles: {},
  assets: [
    { id: "asset-studio", url: "/images/studio-hero.avif", name: "Sunlit studio", alt: "Sunlit creative studio interior", contentType: "image/avif", size: 0, source: "bundled" },
    { id: "asset-architecture", url: "/images/architecture.avif", name: "Modern architecture", alt: "Modern architecture against blue sky", contentType: "image/avif", size: 0, source: "bundled" },
    { id: "asset-orange", url: "/images/orange-building.avif", name: "Orange building", alt: "Orange geometric apartment facade", contentType: "image/avif", size: 0, source: "bundled" },
    { id: "asset-plans", url: "/images/plans.avif", name: "Studio plans", alt: "Architectural plans in a studio", contentType: "image/avif", size: 0, source: "bundled" },
    { id: "asset-team", url: "/images/team.avif", name: "Design team", alt: "Designers collaborating in a studio", contentType: "image/avif", size: 0, source: "bundled" },
  ],
  navigation: [
    { id: "nav-work", label: "Work", href: "#gallery" },
    { id: "nav-services", label: "Approach", href: "#services" },
    { id: "nav-journal", label: "Journal", href: "#blog" },
    { id: "nav-contact", label: "Start a project", href: "#contact" },
  ],
  sections: [
    {
      id: "hero",
      type: "hero",
      content: {
        eyebrow: "INDEPENDENT DIGITAL STUDIO · ZÜRICH",
        title: "The website is the CMS.",
        intro:
          "A premium custom site with one beautifully simple idea: click what you want to change.",
        scrollLabel: "SCROLL TO EXPLORE",
        scrollIcon: "ArrowDownRight",
        noteIcon: "MousePointer2",
        editLabel: "Turn on Edit Site",
        viewLabel: "View the site",
        image: "/images/studio-hero.avif",
        assetId: "asset-studio",
        alt: "Sunlit creative studio interior",
        layers: 36,
        depth: 2.25,
        faceColor: "#f3f0e8",
        depthColor: "#ff5c35",
        tilt: 7.5,
        perspective: 900,
        pointerTracking: true,
        smoothing: 0.14,
        orbit: true,
        orbitSpeed: 0.12,
        fontSize: 112,
        fontWeight: 820,
        shadow: true,
        titleAlign: "left",
        titleMaxWidth: 1180,
        titleOffsetX: 0,
        titleOffsetY: 0,
      },
      style: {
        ...defaultStyle("#163f3a"),
        padding: 54,
        radius: 28,
        align: "left",
      },
      layout: "split",
      animation: defaultAnimation("stagger"),
    },
    {
      id: "services",
      type: "services",
      content: {
        kicker: "BUILT DIFFERENTLY",
        title: "Less machinery. More momentum.",
        intro:
          "No plugin stack to babysit. Just a fast website shaped around your business.",
        items: [
          {
            id: "service-1",
            icon: "MousePointer2",
            label: "01",
            title: "Edit what you see",
            text: "Change copy, images and layouts directly on the live page.",
          },
          {
            id: "service-2",
            icon: "Zap",
            label: "02",
            title: "Static-first speed",
            text: "Fast by default, globally delivered and deliberately lightweight.",
          },
          {
            id: "service-3",
            icon: "ShieldCheck",
            label: "03",
            title: "A smaller surface",
            text: "No sprawling WordPress plugin chain or compatibility maintenance.",
          },
        ],
      },
      style: defaultStyle("#f3f0e8"),
      layout: "grid",
      animation: defaultAnimation("stagger"),
    },
    {
      id: "pixel-swap",
      type: "pixel-swap",
      content: {
        kicker: "A BETTER WORKFLOW",
        title: "From control panel to creative canvas.",
        beforeLabel: "01 / OLD WAY",
        beforeTitle: "Traditional CMS",
        beforeBody: "Open dashboard → Find page → Find field → Preview → Repeat",
        afterLabel: "02 / SANDBOX",
        afterTitle: "Sandbox CMS",
        afterBody: "Click the heading → Type → Done",
        hoverHint: "Hover to transform ↗",
        pixelSize: 54,
        gap: 1,
        pixelRadius: 8,
        spin: 80,
        scale: 0.28,
        duration: 1250,
        pixelDuration: 420,
        pattern: "diagonal",
        randomness: 0.16,
        fade: true,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        trigger: "hover",
        aspectRatio: "16 / 10",
      },
      style: { ...defaultStyle("#d7ff5f"), radius: 28 },
      layout: "split",
      animation: defaultAnimation("fade-up"),
    },
    {
      id: "gallery",
      type: "gallery",
      content: {
        kicker: "SELECTED WORK",
        title: "Designed to be remembered.",
        intro: "A website should feel unmistakably yours.",
        grayscale: true,
        parallax: 0.55,
        tilt: 5,
        expandRatio: 0.55,
        orientation: "horizontal",
        timing: 0.62,
        trigger: "hover",
        items: [
          {
            id: "gallery-1",
            label: "01",
            image: "/images/architecture.avif",
            assetId: "asset-architecture",
            alt: "Modern architecture against blue sky",
            title: "Haus 22",
            text: "Identity · Web",
            color: "#ff5c35",
          },
          {
            id: "gallery-2",
            label: "02",
            image: "/images/orange-building.avif",
            assetId: "asset-orange",
            alt: "Orange geometric apartment facade",
            title: "Raster",
            text: "Campaign · Digital",
            color: "#163f3a",
          },
          {
            id: "gallery-3",
            label: "03",
            image: "/images/plans.avif",
            assetId: "asset-plans",
            alt: "Architectural plans in a studio",
            title: "Common Ground",
            text: "Strategy · Platform",
            color: "#7a6cff",
          },
          {
            id: "gallery-4",
            label: "04",
            image: "/images/team.avif",
            assetId: "asset-team",
            alt: "Designers collaborating in a studio",
            title: "Made Together",
            text: "Studio · Story",
            color: "#e7b4ff",
          },
        ],
      },
      style: {
        ...defaultStyle("#171714"),
        color: "#fffdf8",
        radius: 28,
        gap: 10,
      },
      layout: "accordion",
      animation: defaultAnimation("clip"),
    },
    {
      id: "stats",
      type: "stats",
      content: {
        kicker: "THE QUIET ADVANTAGE",
        title: "Small footprint. Serious results.",
        items: [
          {
            id: "stat-1",
            value: "95+",
            title: "Typical Lighthouse score",
            text: "Performance built into the architecture.",
          },
          {
            id: "stat-2",
            value: "0",
            title: "Plugin updates",
            text: "No compatibility roulette every month.",
          },
          {
            id: "stat-3",
            value: "275",
            title: "Edge cities",
            text: "A global delivery network close to visitors.",
          },
        ],
      },
      style: defaultStyle("#f3f0e8"),
      layout: "grid",
      animation: defaultAnimation("stagger"),
    },
    {
      id: "scroll-expand",
      type: "scroll-expand",
      content: {
        image: "/images/studio-hero.avif",
        assetId: "asset-studio",
        alt: "Bright creative studio with large windows",
        title: "Space to make something distinct.",
        hint: "Keep scrolling ↓",
        startWidth: 72,
        startHeight: 72,
        startRadius: 28,
        endRadius: 0,
        mediaZoom: 1.1,
        scrollDistance: 110,
        smoothing: 0.18,
        overlay: 35,
      },
      style: { ...defaultStyle("#171714"), padding: 28 },
      layout: "cinematic",
      animation: defaultAnimation("none"),
    },
    {
      id: "testimonials",
      type: "testimonials",
      content: {
        kicker: "CLIENT NOTES",
        title: "Clarity feels good.",
        items: [
          {
            id: "quote-1",
            label: "01 / 03",
            title: "“It finally feels like our website belongs to us.”",
            text: "Mara, independent architect",
          },
          {
            id: "quote-2",
            label: "02 / 03",
            title:
              "“We changed the opening hours from a phone between customers.”",
            text: "Leon, neighbourhood restaurant",
          },
          {
            id: "quote-3",
            label: "03 / 03",
            title: "“Fast, calm, and there is nothing mysterious to maintain.”",
            text: "Nina, creative consultancy",
          },
        ],
      },
      style: defaultStyle("#fffdf8"),
      layout: "cards",
      animation: defaultAnimation("stagger"),
    },
    {
      id: "hours",
      type: "hours",
      content: {
        kicker: "COME SAY HELLO",
        title: "Studio hours",
        intro:
          "Tap a day in Edit Mode. Change the schedule right where customers see it.",
        openLabel: "Open now · until",
        closedLabel: "Currently closed",
        todayLabel: "TODAY",
        items: [
          {
            id: "monday",
            label: "Monday",
            title: "09:00",
            text: "17:30",
            value: "open",
            category: "",
          },
          {
            id: "tuesday",
            label: "Tuesday",
            title: "09:00",
            text: "17:30",
            value: "open",
            category: "",
          },
          {
            id: "wednesday",
            label: "Wednesday",
            title: "09:00",
            text: "17:30",
            value: "open",
            category: "By appointment after 16:00",
          },
          {
            id: "thursday",
            label: "Thursday",
            title: "09:00",
            text: "19:00",
            value: "open",
            category: "Late studio",
          },
          {
            id: "friday",
            label: "Friday",
            title: "09:00",
            text: "16:00",
            value: "open",
            category: "",
          },
          {
            id: "saturday",
            label: "Saturday",
            title: "Closed",
            text: "",
            value: "closed",
            category: "",
          },
          {
            id: "sunday",
            label: "Sunday",
            title: "Closed",
            text: "",
            value: "closed",
            category: "",
          },
        ],
      },
      style: { ...defaultStyle("#163f3a"), color: "#fffdf8", radius: 28 },
      layout: "split",
      animation: defaultAnimation("fade-up"),
    },
    {
      id: "blog",
      type: "blog",
      content: {
        kicker: "FIELD NOTES",
        title: "Ideas for a better web.",
        layout: "featured-grid",
        items: [
          {
            id: "post-1",
            category: "Design",
            date: "12 Aug 2026",
            title: "Why the best CMS can disappear completely",
            text: "Tools should get out of the way and leave you with the thing you came to make.",
            image: "/images/team.avif",
            assetId: "asset-team",
            alt: "Designers working together",
            label: "Read story",
            link: "#contact",
            icon: "ArrowUpRight",
          },
          {
            id: "post-2",
            category: "Performance",
            date: "04 Aug 2026",
            title: "The hidden cost of “just one more plugin”",
            text: "Complexity compounds. Static-first architecture changes the equation.",
            image: "/images/architecture.avif",
            assetId: "asset-architecture",
            alt: "Modern architectural facade",
            label: "Read story",
            link: "#contact",
            icon: "ArrowUpRight",
          },
          {
            id: "post-3",
            category: "Studio",
            date: "28 Jul 2026",
            title: "Designing for the person who updates the hours",
            text: "Good editing UX begins with everyday work, not a feature matrix.",
            image: "/images/plans.avif",
            assetId: "asset-plans",
            alt: "Design plans on a table",
            label: "Read story",
            link: "#contact",
            icon: "ArrowUpRight",
          },
        ],
      },
      style: defaultStyle("#f3f0e8"),
      layout: "featured-grid",
      animation: defaultAnimation("stagger"),
    },
    {
      id: "faq",
      type: "faq",
      content: {
        kicker: "THE USEFUL DETAILS",
        title: "Questions, answered.",
        items: [
          {
            id: "faq-1",
            icon: "Plus",
            title: "Is this a template?",
            text: "No. Sandbox demonstrates how each public site can be designed around the business, then connected to the same visual editing system.",
          },
          {
            id: "faq-2",
            icon: "Plus",
            title: "Can my team really edit it?",
            text: "Yes. Text, images, links, collections, hours, layouts and theme controls can all be exposed safely.",
          },
          {
            id: "faq-3",
            icon: "Plus",
            title: "Does static-first mean nothing can change?",
            text: "Not at all. Editing can be dynamic while published pages stay fast, cached and resilient.",
          },
          {
            id: "faq-4",
            icon: "Plus",
            title: "Is hosting always free?",
            text: "Many small sites fit generous platform allowances, but costs depend on traffic, storage and features.",
          },
        ],
      },
      style: defaultStyle("#fffdf8"),
      layout: "list",
      animation: defaultAnimation("fade-up"),
    },
    {
      id: "cta",
      type: "cta",
      content: {
        kicker: "YOUR TURN",
        title: "Make the website yours.",
        intro:
          "Switch on Edit Site. Change this sentence. Move a section. You cannot break the demo.",
        button: "Start editing",
        buttonLink: "#top",
        secondary: "Talk to Sandbox",
        secondaryLink: "mailto:hello@sandbox.studio",
        buttonIcon: "ArrowUpRight",
      },
      style: {
        ...defaultStyle("#ff5c35"),
        color: "#171714",
        radius: 28,
        align: "center",
        padding: 120,
      },
      layout: "centered",
      animation: defaultAnimation("scale"),
    },
  ],
};

export const cloneBaseline = (): SiteConfig =>
  JSON.parse(JSON.stringify(BASELINE)) as SiteConfig;
